import { Button, addToast, useMobile } from '@octano/global-ui';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { HTTP_STATUS_CODE } from '../../../../api/request';
import { downloadFromURL } from '../../../../utils/blob';
import { CertificateType, MyCertificate, ReportTypes } from '../../types';
import {
  getIcebergConfig,
  getMyCertificates,
  downloadPaidCertificate,
  getIcebergPaymentStatus,
} from '../../requests';
import DynamicFormModal from '../DynamicFormModal/DynamicFormModal';
import GeneratingFileModal from '../GeneratingFileModal';
import NoResultsModal from '../NoResultsModal';
import NotRegularStModal from '../NotRegularStModal';
import PaymentModal from '../PaymentModal';
import {
  createReportDownloadHandler,
  createCertificateWithCustomFieldsHandler,
} from './handlerCreator';
import NotInterimGraduatedModal from '../NotInterimGraduated';
import NonStructuredNotCompletedModal from '../NonStructuredNotCompletedModal';

enum ModalType {
  NONE,
  DYNAMIC_FORM,
  GENERATING_FILE,
  NO_CERTIFICATE,
  NOT_REGULAR_STUDENT,
  NOT_GRADUATED_INTERIM,
  CE_NOT_COMPLETED,
  PAYMENT,
}

const DEFAULT_NO_RESULTS_I18N_KEY =
  'reports.enrolledStudent.noResults.description';

//considerar a futuro que el backend entregue el mensaje
const NO_RESULTS_I18N_KEYS: Record<string, string> = {
  [ReportTypes.GradesStudent]: 'reports.gradesStudent.noResults.description',
  [ReportTypes.PdiGradesStudent]: 'reports.gradesStudent.noResults.description',
  'pdi-grades-student-cecapro': 'reports.gradesStudent.noResults.description',
  'pdi-grades-student-oppl': 'reports.gradesStudent.noResults.description',
  [ReportTypes.PdiAcademicProgressStudent]:
    'reports.academicProgressStudent.noResults.description',
};

interface ICertificateActionsProps {
  studyPlanEnrollmentId: number;
  report: CertificateType;
  isDynamic?: boolean;
}

export default function CertificateActions({
  studyPlanEnrollmentId,
  report,
  isDynamic = false,
}: ICertificateActionsProps) {
  const { t } = useTranslation();
  const isMobile = useMobile();

  const [openedModal, setOpenedModal] = useState<ModalType>(ModalType.NONE);
  const [icebergEnabled, setIcebergEnabled] = useState(false);
  const [
    pendingCertificate,
    setPendingCertificate,
  ] = useState<MyCertificate | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);

  // Load iceberg config and pending certificates on mount
  useEffect(() => {
    if (!report.paymentRequired) {
      return;
    }

    const loadPaymentData = async () => {
      const { data: configData } = await getIcebergConfig();
      if (configData) {
        setIcebergEnabled(configData.enabled);
      }

      const { data: certs } = await getMyCertificates();
      if (certs) {
        // Find pending certificate for this type
        const pending = certs.find(
          (c) =>
            c.certificateType?.code === report.code &&
            c.paymentStatus === 'pending_payment',
        );
        if (pending) {
          setPendingCertificate(pending);
        }
      }
    };

    loadPaymentData();
  }, [report.paymentRequired, report.code]);

  const handleReportDownload = createReportDownloadHandler({
    beforeDownload: () => setOpenedModal(ModalType.GENERATING_FILE),
    afterDownload: ({
      status,
      error,
    }: {
      url: string;
      status: number;
      error?: any;
    }) => {
      if (error) {
        return;
      }
      if (status === HTTP_STATUS_CODE.NO_CONTENT) {
        setOpenedModal(ModalType.NO_CERTIFICATE);
      } else {
        setOpenedModal(ModalType.NONE);
      }
    },
    onSuccess: ({ url, certificateId, paymentRequired }: any) => {
      if (paymentRequired && certificateId) {
        // Certificate needs payment, refresh pending state
        setPendingCertificate({
          id: certificateId,
          paymentStatus: 'pending_payment',
          paymentMethod: null,
          paidAt: null,
          createdAt: new Date().toISOString(),
          certificateType: {
            id: report.id,
            name: report.name,
            code: report.code,
            paymentRequired: true,
            paymentAmount: report.paymentAmount || '0',
          },
        });
        setOpenedModal(ModalType.PAYMENT);
        return;
      }
      if (url) {
        downloadFromURL(url);
      }
    },
    onError: ({ data, status }) => {
      if (data?.error === 'NOT_REGULAR_STUDENT') {
        setOpenedModal(ModalType.NOT_REGULAR_STUDENT);
      } else if (data?.error === 'CE_NOT_COMPLETED') {
        setOpenedModal(ModalType.CE_NOT_COMPLETED);
      } else if (status !== HTTP_STATUS_CODE.NO_CONTENT) {
        const errorCode = data?.error;
        const errorMessage = errorCode
          ? t(
              `reports.errors.${errorCode}`,
              t(`reports.errors.errorGeneratingFile`),
            )
          : t(`reports.errors.errorGeneratingFile`);
        addToast({
          icon: 'error',
          color: 'danger',
          text: errorMessage,
        });
        setOpenedModal(ModalType.NONE);
      }
    },
  });

  const handleCertificateGeneration = createCertificateWithCustomFieldsHandler({
    beforeDownload: () => setOpenedModal(ModalType.GENERATING_FILE),
    afterDownload: ({
      status,
      error,
    }: {
      url: string;
      status: number;
      error?: any;
    }) => {
      const errorData = error?.data;

      if (errorData?.error === 'NOT_GRADUATED_INTERIM') {
        setOpenedModal(ModalType.NOT_GRADUATED_INTERIM);
      } else {
        if (status === HTTP_STATUS_CODE.NO_CONTENT) {
          setOpenedModal(ModalType.NO_CERTIFICATE);
        } else {
          setOpenedModal(ModalType.NONE);
        }
      }
    },
    onSuccess: ({ url, certificateId, paymentRequired }: any) => {
      if (paymentRequired && certificateId) {
        setPendingCertificate({
          id: certificateId,
          paymentStatus: 'pending_payment',
          paymentMethod: null,
          paidAt: null,
          createdAt: new Date().toISOString(),
          certificateType: {
            id: report.id,
            name: report.name,
            code: report.code,
            paymentRequired: true,
            paymentAmount: report.paymentAmount || '0',
          },
        });
        setOpenedModal(ModalType.PAYMENT);
        return;
      }
      if (url) {
        downloadFromURL(url);
      }
      setOpenedModal(ModalType.NONE);
    },
    onError: ({ data }) => {
      if (data?.error === 'NOT_REGULAR_STUDENT') {
        setOpenedModal(ModalType.NOT_REGULAR_STUDENT);
      } else if (data?.error === 'NOT_GRADUATED_INTERIM') {
        // handled in afterDownload
      } else if (data?.error === 'CE_NOT_COMPLETED') {
        setOpenedModal(ModalType.CE_NOT_COMPLETED);
      } else {
        const errorCode = data?.error;
        const errorMessage = errorCode
          ? t(
              `reports.errors.${errorCode}`,
              t(`reports.errors.errorGeneratingFile`),
            )
          : t(`reports.errors.errorGeneratingFile`);
        addToast({
          icon: 'error',
          color: 'danger',
          text: errorMessage,
        });
        setOpenedModal(ModalType.NONE);
      }
    },
  });

  const handleVerify = useCallback(async () => {
    if (!pendingCertificate) {
      return;
    }
    setVerifyLoading(true);

    if (pendingCertificate.paymentMethod === 'iceberg') {
      // Check iceberg status
      const { data } = await getIcebergPaymentStatus(pendingCertificate.id);
      setVerifyLoading(false);

      if (data?.status === 'success') {
        setPendingCertificate((prev) =>
          prev ? { ...prev, paymentStatus: 'paid' } : null,
        );
        addToast({
          icon: 'success',
          color: 'success',
          text: t('reports.payment.paymentConfirmed'),
        });
      } else if (data?.status === 'failed') {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('reports.payment.paymentFailed'),
        });
      } else {
        addToast({
          icon: 'information',
          color: 'warning',
          text: t('reports.payment.paymentStillProcessing'),
        });
      }
    } else {
      // Gateway: reopen payment modal to retry
      setVerifyLoading(false);
      setOpenedModal(ModalType.PAYMENT);
    }
  }, [pendingCertificate, t]);

  const handleDownloadPaid = useCallback(async () => {
    if (!pendingCertificate) {
      return;
    }
    setDownloadLoading(true);

    const { data, error } = await downloadPaidCertificate(
      pendingCertificate.id,
    );
    setDownloadLoading(false);

    if (error || !data?.url) {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('reports.errors.errorDownloadingFile'),
      });
      return;
    }

    downloadFromURL(data.url);
  }, [pendingCertificate, t]);

  const handlePaymentCompleted = useCallback(() => {
    setPendingCertificate((prev) =>
      prev ? { ...prev, paymentStatus: 'paid' } : null,
    );
  }, []);

  const getActionHandler = () => {
    if (isDynamic) {
      return () => setOpenedModal(ModalType.DYNAMIC_FORM);
    }
    return () => handleReportDownload(studyPlanEnrollmentId, report.code);
  };

  const handleFormSubmit = (formData: Record<string, any>) => {
    handleCertificateGeneration(studyPlanEnrollmentId, report.code, formData);
  };

  const handleFormCancel = () => {
    setOpenedModal(ModalType.NONE);
  };

  const handleNoResultsConfirm = () => {
    setOpenedModal(ModalType.NONE);
  };

  const handleNotInterimGraduatedConfirm = () => {
    setOpenedModal(ModalType.NONE);
  };

  const handleCeNotCompletedConfirm = () => {
    setOpenedModal(ModalType.NONE);
  };

  // Determine what button to show
  const renderButton = () => {
    // Has pending certificate with payment
    if (pendingCertificate) {
      if (pendingCertificate.paymentStatus === 'paid') {
        return (
          <Button
            text={t('common.actions.download')}
            icon="download"
            size="sm"
            outlined
            loading={downloadLoading}
            style={{ width: isMobile ? '90%' : 123 }}
            onClick={handleDownloadPaid}
          />
        );
      }

      if (
        pendingCertificate.paymentStatus === 'pending_payment' &&
        pendingCertificate.paymentMethod
      ) {
        // Already started a payment, show "Verify"
        return (
          <Button
            text={t('reports.payment.verify')}
            size="sm"
            outlined
            loading={verifyLoading}
            style={{ width: isMobile ? '90%' : 123 }}
            onClick={handleVerify}
          />
        );
      }

      // Pending but no method yet — show "Pay"
      return (
        <Button
          text={t('reports.payment.goToPay')}
          size="sm"
          style={{ width: isMobile ? '90%' : 160 }}
          onClick={() => setOpenedModal(ModalType.PAYMENT)}
        />
      );
    }

    // No pending certificate — standard flow
    if (isDynamic) {
      return (
        <Button
          text={t(`reports.actions.generateCustom`)}
          icon="download"
          size="sm"
          outlined
          style={{ width: isMobile ? '90%' : 123 }}
          onClick={getActionHandler()}
        />
      );
    }

    return (
      <Button
        text={t(`common.actions.download`)}
        icon="download"
        size="sm"
        outlined
        style={{ width: isMobile ? '90%' : 123 }}
        onClick={getActionHandler()}
      />
    );
  };

  return (
    <>
      <DynamicFormModal
        isOpen={openedModal === ModalType.DYNAMIC_FORM}
        certificateTypeCode={report.code}
        certificateTitle={report.name}
        onSubmit={handleFormSubmit}
        onCancel={handleFormCancel}
      />

      <NotRegularStModal
        isOpen={openedModal === ModalType.NOT_REGULAR_STUDENT}
      />

      <NotInterimGraduatedModal
        isOpen={openedModal === ModalType.NOT_GRADUATED_INTERIM}
        onConfirm={handleNotInterimGraduatedConfirm}
      />

      <GeneratingFileModal isOpen={openedModal === ModalType.GENERATING_FILE} />

      <NoResultsModal
        isOpen={openedModal === ModalType.NO_CERTIFICATE}
        onConfirm={handleNoResultsConfirm}
        texts={{
          title: t(
            NO_RESULTS_I18N_KEYS[report.code] ?? DEFAULT_NO_RESULTS_I18N_KEY,
          ),
          description: '',
        }}
      />

      <NonStructuredNotCompletedModal
        isOpen={openedModal === ModalType.CE_NOT_COMPLETED}
        onConfirm={handleCeNotCompletedConfirm}
      />

      <PaymentModal
        isOpen={openedModal === ModalType.PAYMENT}
        certificateId={pendingCertificate?.id ?? null}
        certificateName={report.name}
        paymentAmount={report.paymentAmount || '0'}
        icebergEnabled={icebergEnabled}
        onClose={() => setOpenedModal(ModalType.NONE)}
        onPaymentCompleted={handlePaymentCompleted}
      />

      <div
        className="d-flex flex-wrap align-items-center justify-content-center"
        style={{ gap: 12 }}
      >
        {renderButton()}
      </div>
    </>
  );
}
